/* eslint-disable @alice-health/w-list */
import { SlidePanel } from '@/global/components/SlidePanel'
import { useDrawer, usePreventPageScroll } from '@/global/hooks'
import { WIcon, WTextfield, WTitle } from '@alice-health/wonderland-react'
import {
  FunctionComponent,
  useCallback,
  useEffect,
  useRef,
  useState
} from 'react'
import { useAddressSearch } from '@/modules/accredited-establishments/components/Filters/hooks'
import { FilterItem } from '@/modules/accredited-establishments/api/types'
import { DRAWER_CLASS_NAME } from '@/modules/accredited-establishments/components/Filters/Filters.constants'
import { ADDRESS_SEARCH_MODAL_ID } from './AddressSearchModal.constants'
import { Skeleton } from '@/global/components/Skeleton'

const AddressSearchModal: FunctionComponent = () => {
  const { toggle, isOpen } = useDrawer(ADDRESS_SEARCH_MODAL_ID)
  const [searchTerm, setSearchTerm] = useState<string>('')
  const textfieldRef = useRef<HTMLWTextfieldElement>(null)

  const { address, addresses, updateAddress, onChange, isLoading } =
    useAddressSearch()

  usePreventPageScroll(isOpen)

  useEffect(() => {
    if (address) {
      setSearchTerm(address.title)
    }
  }, [address])

  useEffect(() => {
    if (isOpen) {
      textfieldRef.current
        ?.querySelector('input')
        ?.focus({ preventScroll: true })
    }
  }, [isOpen])

  const triggerSearch = useCallback(
    (search: string) => onChange(search),
    [onChange]
  )

  const onSelectOption = (address: FilterItem) => {
    updateAddress(address)
    toggle()
  }

  const onChangeSearchTerm = (event: { detail: string }) => {
    setSearchTerm(event.detail)
    triggerSearch(event.detail)
  }

  return (
    <SlidePanel
      id={ADDRESS_SEARCH_MODAL_ID}
      data-testid='address-search-modal'
      position='bottom'
      className={DRAWER_CLASS_NAME}
    >
      <div className='flex flex-col h-full py-gl-spacing-08 px-gl-spacing-06 gap-gl-spacing-10'>
        <div className='flex items-center h-gl-spacing-12'>
          <WTitle size='xsmall'>Alterar localização</WTitle>
        </div>

        <WTextfield
          ref={textfieldRef}
          value={searchTerm}
          onWChange={onChangeSearchTerm}
          clearable
          leadingIcon='icSearch'
          placeholder='Buscar nova localização'
        />

        {isLoading ? (
          <div className='flex flex-col gap-gl-spacing-04'>
            {Array.from({ length: 5 }).map((_, index) => (
              <Skeleton
                key={index}
                width='w-full'
                height='h-gl-spacing-12'
                variant='rectangular'
              />
            ))}
          </div>
        ) : (
          <ul data-testid='address-search-results'>
            {addresses?.items.map(address => (
              <li
                key={address.value}
                className='flex items-center gap-gl-spacing-03 py-gl-spacing-04'
                onClick={() => onSelectOption(address)}
              >
                <WIcon
                  className='text-gl-color-shades-gray-30'
                  icon='icLocalPin'
                  size='large'
                />

                {address.title}
              </li>
            ))}
          </ul>
        )}
      </div>
    </SlidePanel>
  )
}

export { AddressSearchModal }
